import { Box } from '@mui/material';
import Grid from '@mui/material/Grid';
import { Fragment, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Description from '/@/components/catalog/description';
import ProductBorderImage from '/@/components/catalog/product-border-image';
import Technologies from '/@/components/catalog/technologies';
import IconText from '/@/components/icon-text';
import Pills from '/@/components/pills';
import colors from '/@/components/theme-provider/colors';
import { _ } from '/@/locale';
import { getSessionCountry, getUser } from '/@/store/session/selectors';
import Title from '/@/v3/components/title';
import { APP_SIDEBAR_WIDTH } from '../../app-navbar/sidebar';
import styles from '../styles.module.css';

const ProductDescription = ({ type, product, isMobile, isDesktopMd, itemId, zoneId, slug }) => {
    const [scroll, setScroll] = useState(false);
    const sessionCountry = useSelector(getSessionCountry);
    const showBrand = product.brand && product.brand.type === 'soy';

    const showTechnologies =
        type !== 'crop_protection' &&
        Boolean(product.technologies) &&
        product.technologies.filter((e) => e.codename !== 'DKProtect').length > 0;

    const productTechnologies =
        product.technologies &&
        product.technologies
            .map((tec) => {
                const brandImage = tec.logos.find((logo) => logo.brandId === product.brand.id);
                return {
                    label: tec.id,
                    imgPath: brandImage && brandImage.image,
                    logoPath: tec.logoImage,
                };
            })
            .filter(({ imgPath }) => imgPath !== undefined);

    const valueForGridItem = (showTechnologies && productTechnologies.length) || showBrand ? 8 : 12;
    const notes = product.notes ? product.notes.map((note) => note.text) : [];
    const isSoyBR = type === 'soy' && sessionCountry.code === 'BR';
    const typeForPills = (product.type && product.type.name) || product.type;
    const isCropProtection = type === 'crop_protection';
    const el = document.getElementsByClassName('root');

    useEffect(() => {
        const controlNavbar = () => {
            if (el[0] && el?.[0]?.getBoundingClientRect().y < 0) {
                setScroll(true);
            } else {
                setScroll(false);
            }
        };

        if (!isMobile) {
            window.addEventListener(
                'wheel',
                () => {
                    controlNavbar();
                },
                false
            );
        } else {
            setScroll(true);
        }
    }, [isMobile]);

    const user = useSelector(getUser);

    return (
        <Grid
            container
            justifyContent='center'
            alignItems={!isMobile ? 'stretch' : 'center'}
            spacing={4}
            sx={{ mt: { md: 1, xs: 2 } }}
        >
            <div className='root' style={{ height: 10 }} />
            {scroll && isCropProtection && (
                <Fragment>
                    <Grid
                        sx={{
                            py: { xs: 1, md: 3 },
                            px: { xs: 2, lg: 4 },
                            position: 'fixed',
                            left: !user ? 0 : { xs: 0, sm: APP_SIDEBAR_WIDTH },
                            top: 0,
                            width: '100vw',
                            background: 'white',
                            zIndex: 10,
                            borderBottom: `1px solid ${colors.gray[800]}`,
                        }}
                    >
                        <Grid
                            item
                            container
                            direction='row'
                            alignItems='center'
                            justifyContent='space-between'
                            sx={isDesktopMd && { maxWidth: '1200px', margin: 'auto' }}
                        >
                            <Grid
                                item
                                container
                                sx={{ backgroundColor: 'white', gap: { xs: 2, md: 5 }, width: 'auto' }}
                            >
                                <Grid item>
                                    <Title size={isMobile ? 'md' : 'xl'} italic bolder>
                                        {product.name}
                                    </Title>
                                </Grid>
                                <Grid item container alignItems='center' sx={{ width: 'auto' }}>
                                    <Pills type={typeForPills} crops={product?.crops} />
                                </Grid>
                            </Grid>
                            <Grid item>
                                <ProductBorderImage
                                    srcImage={product.image}
                                    classNameLogoContainer={styles.smallLogoContainer}
                                    classNameLogo={styles.smallLogo}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Box sx={{ pt: 8 }} />
                </Fragment>
            )}

            <Grid item xs={12} md={valueForGridItem}>
                <Title size='sm' className={styles.titleGray}>
                    {_('Descripción')}
                </Title>
                <Description
                    scroll={scroll}
                    product={product}
                    type={type}
                    itemId={itemId}
                    zoneId={zoneId}
                    slug={slug}
                />
            </Grid>
            {showBrand && (
                <Grid item xs={12} md={4}>
                    <Title size='sm' className={styles.titleGray}>
                        {_('Marca')}
                    </Title>
                    <Grid
                        container
                        direction='row'
                        justifyContent='center'
                        alignItems='center'
                        sx={{ border: 1, borderRadius: 2, borderColor: colors.gray[800], height: '100%', py: 3 }}
                    >
                        <Grid
                            item
                            xs={1}
                            sx={{
                                backgroundImage: `url(${product.brand.logo})`,
                                backgroundSize: 'contain',
                                backgroundRepeat: 'no-repeat',
                                padding: '100px',
                            }}
                        />
                    </Grid>
                </Grid>
            )}
            {showTechnologies && !!productTechnologies.length && (
                <Grid item xs={12} md={4}>
                    <Title size='sm' className={styles.titleGray}>
                        {_('Tecnologías Disponibles')}
                    </Title>
                    <Technologies product={product} productTechnologies={productTechnologies} />
                </Grid>
            )}
            {isSoyBR && notes.length !== 0 && (
                <Grid item xs={12}>
                    <Title size='sm' className={styles.titleGray}>
                        {_('Resumen de recomendación de producto')}
                    </Title>
                    <Grid
                        container
                        direction='row'
                        justifyContent='center'
                        alignItems='center'
                        sx={{ border: 1, borderRadius: 2, borderColor: colors.gray[800], height: '100%', p: 4 }}
                    >
                        {notes.map((note, index) => {
                            const lastOne = index === notes.length - 1;
                            return (
                                <Grid key={note.es} item xs={12} sx={{ mb: lastOne ? 0 : 2 }}>
                                    <IconText bigFontSize translate text={note} withTheme={false} />
                                </Grid>
                            );
                        })}
                    </Grid>
                </Grid>
            )}
        </Grid>
    );
};

export default ProductDescription;
